import React, { useState } from "react";
import { Modal, Form, Input, Select, Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { campaignAPI, CampaignStatusPhase, CampaignType, getStatusTypeFromPhase } from "@/api/campaign";
import type { CampaignData } from "@/api/campaign";
import { Icon } from "@/shared/components/Icon/Icon";
import { CURRENCY_OPTIONS } from "@/shared/utils/currency";
import { formatBudgetValue } from "@/shared/utils/budget";

interface Props {
	open: boolean;
	campaigns: CampaignData[];
	onClose: () => void;
	onCreated: (created: CampaignData) => void;
	onSearchDuplicate: (name: string) => void;
}

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background: #f3f5ff !important;
		box-shadow: 0px 12px 24px 0px #1559a53d !important;
		border-radius: 24px;
		padding: 0 !important;
	}

	.ant-form-item-label > label {
		color: #707070 !important;
		font-weight: 400 !important;
		height: auto !important;
		line-height: 1.2 !important;
		padding-bottom: 4px !important;
	}

	.ant-form-item {
		margin-bottom: 0 !important;
	}

	.ant-form-item-label {
		padding: 0 !important;
	}

	/* Target Type and Status Select fields Specifically */
	.ant-form-item:has(select),
	.ant-form-item:has(.ant-select) {
		margin-bottom: 16px !important;
	}

	.ant-input,
	.ant-input-number,
	.ant-select-selector {
		border: 1px solid #cdd4f6 !important;
		border-radius: 8px !important;
		padding: 0 12px !important;
		height: 44px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-input-number {
		width: 100% !important;
		box-shadow: none !important;
	}

	.ant-input-number .ant-input-number-input-wrap {
		display: flex !important;
		align-items: center !important;
		height: 100% !important;
	}

	.ant-input-number .ant-input-number-input {
		height: 44px !important;
		line-height: 44px !important;
		padding: 0 !important;
	}

	.ant-input-number:hover,
	.ant-input-number:focus,
	.ant-input-number-focused {
		border-color: #cdd4f6 !important;
		box-shadow: none !important;
	}

	.ant-select .ant-select-selector {
		padding-right: 40px !important;
	}

	.ant-select-selection-item {
		padding: 0 !important;
		line-height: 44px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-select-single .ant-select-arrow {
		inset-inline-end: 12px !important;
		top: 0 !important;
		transform: none !important;
		height: 44px !important;
		width: 16px !important;
		margin: 0 !important;
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		line-height: 1 !important;
	}

	.ant-select-single .ant-select-arrow .anticon {
		display: inline-flex !important;
		align-items: center !important;
		justify-content: center !important;
		line-height: 1 !important;
		vertical-align: middle !important;
	}

	.ant-select-single .ant-select-arrow svg {
		display: block !important;
	}

	.ant-select-selection-search {
		inset-inline-start: 12px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-select-selection-placeholder {
		line-height: 44px !important;
		display: flex !important;
		align-items: center !important;
	}

	.currency-select.ant-select .ant-select-selector {
		padding-left: 16px !important;
		height: 44px !important;
		border-radius: 8px !important;
	}

	.currency-select.ant-select .ant-select-selection-search {
		inset-inline-start: 0px !important;
	}

	.currency-select.ant-select .ant-select-selection-search-input {
		padding-left: 0 !important;
	}
`;

const ModalContent = styled.div`
	padding: 32px;
`;

const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 32px;
`;

const FormContainer = styled(Form)`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const HeaderTitle = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const TitleText = styled.div`
	font-weight: 600;
	font-size: 24px;
	line-height: 100%;
	vertical-align: middle;
	color: #000;
`;

const CancelButton = styled(Button)`
	flex: 1 !important;
	height: 48px !important;
	border-radius: 24px !important;
	font-weight: 600 !important;
	font-size: 16px !important;
	border: 2px solid #3a0ca3 !important;
	color: #3a0ca3 !important;
	transition: all 0.2s !important;
	box-sizing: border-box !important;

	&:hover,
	&:focus-visible {
		box-shadow: inset 0 0 0 2px #3a0ca3 !important;
		border-color: #3a0ca3 !important;
		color: #3a0ca3 !important;
	}

	&:active {
		background: rgba(58, 12, 163, 0.05) !important;
	}
`;

const btnStyle = (active: boolean): React.CSSProperties => ({
	flex: 1,
	height: 48,
	borderRadius: 24,
	fontWeight: 600,
	fontSize: 16,
	background: active ? "#3A0CA3" : "#e0e0e0",
	color: active ? "#fff" : "#888",
	border: "none",
});

const BudgetGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1.4fr) minmax(160px, 0.8fr);
	gap: 16px;
`;

export const CreateCampaignModal: React.FC<Props> = ({ open, campaigns, onClose, onCreated, onSearchDuplicate }) => {
	const [form] = Form.useForm();
	const [formValid, setFormValid] = useState(false);
	const [creating, setCreating] = useState(false);
	const [dupModalOpen, setDupModalOpen] = useState(false);
	const [dupName, setDupName] = useState("");

	const close = () => {
		form.resetFields();
		setFormValid(false);
		onClose();
	};

	const doCreate = async (fv: any) => {
		try {
			setCreating(true);
			const normalizedTotalBudget = fv.totalBudget == null ? "" : String(fv.totalBudget).replace(/,/g, "").trim();
			const totalBudget = normalizedTotalBudget === "" ? undefined : Number(normalizedTotalBudget);
			const created = await campaignAPI.create({
				name: fv.campaignName,
				projectCode: fv.projectCode || "CREATORVERSE",
				status: getStatusTypeFromPhase(fv.status),
				statusPhase: fv.status,
				type: fv.type,
				amplifyTeam: [],
				lead: [],
				location: [],
				client: fv.client ? [fv.client] : [],
				lastModificationTime: new Date().toISOString(),
				totalBudget: Number.isFinite(totalBudget) ? totalBudget : undefined,
				currency: fv.currency || "USD",
			});
			message.success("Campaign created successfully!");
			close();
			onCreated(created);
		} catch {
			message.error("Failed to create campaign");
		} finally {
			setCreating(false);
		}
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			if (!values.projectCode?.trim()) {
				values.projectCode = "CREATORVERSE";
				form.setFieldsValue({ projectCode: "CREATORVERSE" });
			}
			const dup = campaigns.find(
				(c) =>
					c.name.toLowerCase() === values.campaignName.trim().toLowerCase() &&
					c.projectCode.toLowerCase() === values.projectCode.trim().toLowerCase(),
			);
			if (dup) {
				setDupName(dup.name);
				setDupModalOpen(true);
				return;
			}
			await doCreate(values);
		} catch (e) {
			console.error(e);
		}
	};

	return (
		<>
			<StyledModal open={open} footer={null} onCancel={close} centered width={520} closable={false} title={null}>
				<ModalContent>
					<ModalHeader>
						<HeaderTitle>
							<Icon src="/icon/menu_campaign.svg" size={36} color="#454545" />
							<TitleText>New Campaign</TitleText>
						</HeaderTitle>
						<CloseOutlined
							style={{ fontSize: 18, cursor: "pointer", color: "#666", marginTop: 8 }}
							onClick={close}
						/>
					</ModalHeader>
					<FormContainer
						form={form}
						layout="vertical"
						initialValues={{ currency: "USD" }}
						onValuesChange={() => {
							const v = form.getFieldsValue();
							setFormValid(!!v.campaignName?.trim() && !!v.type && !!v.status);
						}}
					>
						<Form.Item
							name="campaignName"
							label="Campaign Name"
							rules={[{ required: true, message: "Please enter campaign name!" }]}
						>
							<Input />
						</Form.Item>
						<Form.Item name="projectCode" label="Project Code (Optional)">
							<Input />
						</Form.Item>
						<Form.Item name="client" label="Brand (Optional)">
							<Input />
						</Form.Item>
						<Form.Item
							name="type"
							label="Type"
							rules={[{ required: true, message: "Please select type!" }]}
						>
							<Select options={Object.values(CampaignType).map((t) => ({ label: t, value: t }))} />
						</Form.Item>
						<Form.Item
							name="status"
							label="Status"
							rules={[{ required: true, message: "Please select status!" }]}
						>
							<Select options={Object.values(CampaignStatusPhase).map((p) => ({ label: p, value: p }))} />
						</Form.Item>
						<BudgetGrid>
							<Form.Item
								name="totalBudget"
								label="Total Budget"
								normalize={(value: string) => formatBudgetValue(value)}
								rules={[
									{
										validator: async (_rule, value) => {
											if (value == null || value === "") {
												return;
											}

											const stripped = String(value).replace(/,/g, "");
											// allow integers or decimals with up to two fractional digits
											if (!/^\d+(?:\.\d{1,2})?$/.test(stripped)) {
												throw new Error("Budget must be numeric");
											}
										},
									},
								]}
							>
								<Input inputMode="numeric" placeholder="0" />
							</Form.Item>
							<Form.Item name="currency" label="Currency">
								<Select
									className="currency-select"
									showSearch
									options={CURRENCY_OPTIONS}
									placeholder="Select"
									// show symbol in the select value
									optionLabelProp="label"
									// allow searching by currency code
									optionFilterProp="value"
								/>
							</Form.Item>
						</BudgetGrid>
						<div style={{ display: "flex", gap: 16, marginTop: 24 }}>
							<CancelButton onClick={close}>Cancel</CancelButton>
							<Button
								style={btnStyle(formValid)}
								onClick={handleSubmit}
								disabled={!formValid || creating}
								loading={creating}
							>
								Create
							</Button>
						</div>
					</FormContainer>
				</ModalContent>
			</StyledModal>
			<Modal
				title="Duplicate Campaign Detected"
				open={dupModalOpen}
				footer={null}
				onCancel={() => setDupModalOpen(false)}
				centered
				width={550}
			>
				<p style={{ fontSize: 16, marginBottom: 24 }}>
					A campaign with the same <strong>name</strong> and <strong>project code</strong> already exists.
					<br />
					<br />
					Do you still want to add it? Otherwise, please search for the existing campaign.
				</p>
				<div style={{ display: "flex", gap: 16 }}>
					<Button
						style={{ flex: 1, height: 48, borderRadius: 24, fontWeight: 600, fontSize: 16 }}
						onClick={() => {
							setDupModalOpen(false);
							close();
							onSearchDuplicate(dupName);
						}}
					>
						Search Campaign
					</Button>
					<Button
						style={btnStyle(true)}
						onClick={() => {
							setDupModalOpen(false);
							doCreate(form.getFieldsValue());
						}}
					>
						Create Anyway
					</Button>
				</div>
			</Modal>
		</>
	);
};
